import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';
import {
  boxShadow,
  fontSize,
  fontWeights,
  minBreakpointQuery,
  standardColours,
  zIndexLayers,
  standardTransition,
} from '../styles';
import { Link } from './ui';
import arrow from '../images/arrow-icon.svg';

const StyledMapLocation = styled.article`
  ${({ $isLarge }) => {
    if ($isLarge) {
      return css`
        --tooltip-width: 280px;
        padding: 15px;
        top: 0;
        right: 0;
        opacity: 1;
        position: relative;

        ${minBreakpointQuery.tiny`
          --tooltip-width: 300px;
        `}

        ${minBreakpointQuery.tsmall`
          --tooltip-width: 320px;
          padding: 20px;
        `}

        ${minBreakpointQuery.small`
          --tooltip-width: 340px;
          padding: 20px;
        `}

        ${minBreakpointQuery.smedium`
         --tooltip-width: 260px;
          padding: 23px;
          position: absolute;
        `}

        ${minBreakpointQuery.medium`
          --tooltip-width: 300px;
          padding: 25px;
        `}

        ${minBreakpointQuery.large`
          --tooltip-width: 360px;
          padding: 30px;
        `}
      `;
    } else {
      return css`
        --tooltip-width: 180px;
        padding: 15px;
        top: 0;
        left: -5%;
        opacity: 0;
        transform: translate(-25%, -100%);
        position: absolute;
      `;
    }
  }}

  z-index: ${zIndexLayers.top};
  background-color: ${standardColours.white};
  width: var(--tooltip-width);
  text-align: center;
  ${boxShadow};
  transition: ${standardTransition('opacity')};
  border-radius: calc(var(--tooltip-width) / 2);
`;

const StyledTooltipImage = styled(GatsbyImage)`
  border-radius: 50%;
  margin-bottom: 20px;
  object-fit: fill;
  aspect-ratio: 1/1;
`;

const StyledTooltipHeading = styled.h3`
  ${minBreakpointQuery.small`
    ${fontSize(20)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(24)};
  `}

  ${({ $isLarge }) => {
    if ($isLarge) {
      return css`
        ${fontSize(20)};

        ${minBreakpointQuery.tiny`
          ${fontSize(22)};
        `}

        ${minBreakpointQuery.tsmall`
          ${fontSize(24)};
        `}

        ${minBreakpointQuery.small`
          ${fontSize(26)};
        `}

        ${minBreakpointQuery.medium`
          ${fontSize(28)};
        `}

        ${minBreakpointQuery.large`
          ${fontSize(30)};
        `}
      `;
    }
  }}
`;

const StyledTooltipContent = styled.div`
  ${minBreakpointQuery.medium`
    ${fontSize(18)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(20)};
  `}
`;

const StyledTooltipLocation = styled.p``;

const StyledTooltipAcres = styled.p``;

const StyledTooltipText = styled.p`
  max-width: 300px;
  margin-top: 20px;
  margin-bottom: 25px;
  ${fontSize(12)};

  ${minBreakpointQuery.small`
    ${fontSize(14)};
    margin-bottom: 30px;
  `}

  ${minBreakpointQuery.large`
    ${fontSize(16)};
    margin-bottom: 35px;
  `}

  ${({ $isLarge }) => {
    if ($isLarge) {
      return css`
        ${fontSize(14)};

        ${minBreakpointQuery.tiny`
          ${fontSize(15)};
        `}

        ${minBreakpointQuery.tsmall`
          ${fontSize(16)};
        `}

        ${minBreakpointQuery.small`
          ${fontSize(17)};
        `}

        ${minBreakpointQuery.medium`
          ${fontSize(18)};
        `}

        ${minBreakpointQuery.large`
          ${fontSize(20)};
        `}
      `;
    }
  }}
`;

const StyledTooltipLink = styled(Link)`
  font-weight: ${fontWeights.semibold};
  display: inline-block;
`;

const StyledArrow = styled.img`
  width: 60px;
  display: block;
  margin: 10px auto 0;

  ${minBreakpointQuery.medium`
    width: 80px;
  `}

  ${minBreakpointQuery.large`
    width: 100px;
  `}
`;

const MapLocation = ({
  image,
  name,
  location,
  acres,
  content,
  link,
  isLarge,
  ...props
}) => (
  <StyledMapLocation $isLarge={isLarge} {...props}>
    <StyledTooltipImage
      image={image.gatsbyImageData}
      alt={image.title}
      $isLarge={isLarge}
    />
    <StyledTooltipHeading $isLarge={isLarge}>{name}</StyledTooltipHeading>
    <StyledTooltipContent $isLarge={isLarge}>
      {location && <StyledTooltipLocation>{location}</StyledTooltipLocation>}
      {acres && <StyledTooltipAcres>{acres} acres</StyledTooltipAcres>}
      {content && (
        <StyledTooltipText $isLarge={isLarge}>{content}</StyledTooltipText>
      )}
    </StyledTooltipContent>
    {isLarge && (
      <StyledTooltipLink to={link.pageUrl}>
        View Case Study
        <StyledArrow src={arrow} alt="Arrow Icon" />
      </StyledTooltipLink>
    )}
  </StyledMapLocation>
);

export default MapLocation;
